import React, { useState, useEffect, useReducer } from 'react';
import { Tooltip, TooltipButton, IconInfo } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import { parseHtml, checkIfValueExist, checkIfMandatoryValidationExists } from '../../utils/util';
import { validateCheckbox } from '../../Validations/Validations';

const getVal = require('lodash/get');

/**
 * returns DLS checkbox compoent
 * @param {*} props
 */
/* eslint complexity: ["error", 21] -- need to increase complexity */
const Checkbox = (props) => {
  const {
    fieldType,
    fieldId,
    fieldLabel,
    cssProperties,
    fieldValidation,
    checkConsent = {},
    setSelectedOptions,
    selectedOptions = {},
    isHiddenRequired = true,
    isMultipleTabsData = false,
    formSubmissionPayload,
    setFormSubmissionPayload,
    selectedOptionLabels,
    setSelectedOptionLabels,
    filledFormData,
    buttonsToDisable,
    disabledButtons,
    setDisabledButtons,
    uncheckedValue = 'No',
    comments,
    setCurPageValues,
    curPageValues,
    validations = [],
    groupDynamicRequired,
    isFilledValueDisabled,
    defaultValue = 'No',
    dispatchIdsState,
    idsToClear,
    fieldDynamicRequired = null,
    setShowPopUp,
  } = props;
  const [optionCheck, setOptionCheck] = useState(false);
  const [value, setValue] = useState(uncheckedValue);
  const [fieldIdValue, setFieldIdValue] = useState('');
  const [forceRequired, setForceRequired] = useState(false);
  const customInvalidMsg = fieldValidation && fieldValidation.customInvalidMsg
    ? fieldValidation.customInvalidMsg : 'Please tick this box if you want to proceed';
  const [showCustomInvalidMsg, setShowCustomInvalidMsg] = useState(false);
  const [customValidations, setCustomValidations] = useState([...validations]);
  const [validationErrState, dispatchErrState] = useReducer((prevState, payload) => ({ ...prevState, ...payload }), { validationErr: '', isValid: true });
  const [valueChanged, setValueChanged] = useState(false);

  const performCustomValidations = (params) => {
    // const dataObject = { memberInfo, formSubmissionPayload, curPageValues,  };
    const { value: checkBoxValue, validationsToPerform } = params;
    /* eslint-disable max-len -- need */
    const invalidMsg = validateCheckbox(checkBoxValue, validationsToPerform, { isFieldRequired: fieldValidation.required });
    /* eslint-enable max-len -- need */
    if (invalidMsg) {
      dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
    } else {
      dispatchErrState({ validationErr: '', isValid: true });
    }
  };

  useEffect(() => {
    // This will clear the mandatory error message for this input
    if (fieldDynamicRequired && fieldValidation && !fieldValidation.required) {
      const mandatoryValidation = validations.find((val) => val.type === 'mandatoryField');
      if (mandatoryValidation) {
        performCustomValidations({ inputVal: optionCheck, validationsToPerform: validations });
      }
    }
  }, [fieldValidation.required]);

  const groupDynamicValidations = (custValidations, group) => {
    const {
      ids: groupIds, operation, errorMsg, uncheckedValue: uncheckedValueGroup,
    } = group;
    const valueExists = checkIfValueExist(groupIds, formSubmissionPayload, uncheckedValueGroup);
    const shouldMakeitRequired = operation === 'isEmpty' ? !valueExists : valueExists;
    setForceRequired(shouldMakeitRequired);

    if (shouldMakeitRequired) {
      if (!checkIfMandatoryValidationExists(custValidations)) {
        setCustomValidations([{
          type: 'mandatoryField',
          onInvalidMsg: errorMsg,
        }, ...customValidations]);
      }
    } else {
      const removedMandatoryValidation = custValidations.filter((customValidation) => customValidation.type !== 'mandatoryField');
      setCustomValidations([...removedMandatoryValidation]);
      dispatchErrState({ validationErr: '', isValid: true });
    }
  };

  useEffect(() => {
    setFieldIdValue(fieldId);
  }, [fieldId]);

  useEffect(() => {
    if (fieldId !== fieldIdValue) {
      setValue('No');
      setOptionCheck(false);
    }

    if (filledFormData && filledFormData[fieldId] && (typeof isFilledValueDisabled === 'undefined' || !isFilledValueDisabled)) {
      setValue(filledFormData[fieldId]);
      if (filledFormData[fieldId] === 'Yes') {
        setOptionCheck(true);
      } else {
        setOptionCheck(false);
      }
    }

    if (defaultValue === 'Yes') {
      setValue('Yes');
      setOptionCheck(true);
    }

    if (isMultipleTabsData) {
      if (selectedOptions && selectedOptions[fieldId]) {
        setValue(selectedOptions[fieldId]);
        setOptionCheck(true);
      }
    }
  }, [fieldId]);

  useEffect(() => {
    let val = optionCheck;
    if (fieldValidation && fieldValidation.updatePayloadOnChange) {
      val = uncheckedValue;
      if (optionCheck) {
        val = 'Yes';
      }
      formSubmissionPayload[fieldId] = val;
      setFormSubmissionPayload({
        ...formSubmissionPayload,
        elemId: null,
      });
    }
    if (buttonsToDisable && buttonsToDisable.length > 0) {
      buttonsToDisable.forEach((btnId) => {
        if (optionCheck) {
          if (disabledButtons[btnId] && disabledButtons[btnId][fieldId]) {
            delete disabledButtons[btnId][fieldId];
          }
        } else if (disabledButtons[btnId]) {
          disabledButtons[btnId] = { ...disabledButtons[btnId], [fieldId]: 1 };
        } else {
          disabledButtons[btnId] = { [fieldId]: 1 };
        }
      });
      setDisabledButtons({ ...disabledButtons });
    }
    setCurPageValues({ ...curPageValues, [fieldId]: val });
  }, [optionCheck]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) {
      setValue('');
      setOptionCheck(false);
      delete idsToClear.ids[fieldId];
      dispatchIdsState({ toggleState: idsToClear.toggleState, ids: idsToClear });
      delete filledFormData[fieldId];
      setFormSubmissionPayload({ ...filledFormData, elemId: null });
    }
  }, [idsToClear.toggleState]);

  useEffect(() => {
    if (groupDynamicRequired) {
      const { group } = groupDynamicRequired;
      if (group) {
        const { ids } = group;
        if (ids.length > 0) {
          groupDynamicValidations(customValidations, group);
        }
      }
    }
  }, [curPageValues]);

  /**
   * handle checkbox click
   */
  const handleCheck = (e) => {
    const optionId = e.currentTarget.id;
    const removedOption = { ...selectedOptions };
    const optionLabels = { ...selectedOptionLabels };
    if (isMultipleTabsData) {
      if (Object.keys(selectedOptions).length === 0) {
        setSelectedOptions({ [optionId]: `${fieldLabel}` });
        setSelectedOptionLabels({ [optionId]: fieldLabel });
        e.target.value = 'Yes';
        // handleOnChangeEvents(onElementChange, e);
      } else if (selectedOptions[optionId]) {
        delete removedOption[optionId];
        delete optionLabels[optionId];
        setSelectedOptions(removedOption);
        // setSelectedOptionLabels(optionLabels);
        setFormSubmissionPayload({
          // ...removedOption,
          ...{ elemId: null, selectedOptionsLength: Object.keys(removedOption).length },
        });
      } else {
        setSelectedOptions({ ...selectedOptions, ...{ [optionId]: `${fieldLabel}` } });
        // setSelectedOptionLabels({ ...selectedOptionLabels, ...{ [optionId]: fieldLabel } });
        e.target.value = 'Yes';
        let optionsPayload = {};
        // handleOnChangeEvents(onElementChange, e);
        if (Object.keys(selectedOptions).length === 4) {
          let indexCounter = 0;
          /* eslint-disable no-restricted-syntax -- need */
          for (const key in selectedOptions) {
            if (key) {
              indexCounter += 1;
              const optionsPayloadObject = {
                [`merchant${indexCounter}Name`]: selectedOptions[key],
                [`merchant${indexCounter}Id`]: key,
              };
              optionsPayload = { ...optionsPayload, ...optionsPayloadObject };
            }
          }
          /* eslint-enable no-restricted-syntax -- need */
          setFormSubmissionPayload({
            ...formSubmissionPayload,
            ...{
              elemId: null,
              selectedOptionsLength: Object.keys(selectedOptions).length + 1,
              ...optionsPayload,
              ...{
                [`merchant${indexCounter + 1}Name`]: fieldLabel,
                [`merchant${indexCounter + 1}Id`]: optionId,
              },
            },
          });
        } else {
          setFormSubmissionPayload({
            ...formSubmissionPayload,
            ...{ elemId: null, selectedOptionsLength: Object.keys(selectedOptions).length + 1 },
          });
        }
      }
    }

    if (!optionCheck) {
      if (fieldId === 'userConsent') {
        checkConsent(true);
      }
      setValue('Yes');
      setOptionCheck(true);
      if (fieldValidation?.triggerPopUp) {
        setShowPopUp(fieldValidation.triggerPopUp);
      }
      if (customValidations.length > 0) {
        performCustomValidations({ value: true, validationsToPerform: customValidations });
      }
    } else {
      if (fieldId === 'userConsent') {
        checkConsent(false);
      }
      setOptionCheck(false);
      setValue('No');
    }

    // To hide review tool comments
    setValueChanged(true);
  };

  const invalidHandler = (e) => {
    // e.target.setCustomValidity(' ');
    if (customValidations.length > 0) {
      e.preventDefault();
      performCustomValidations({ value: false, validationsToPerform: customValidations });
    } else if (fieldValidation && fieldValidation.hideDefaultValidationMsg) {
      e.preventDefault();
      document.querySelector(`#${fieldId}`).focus();
      setShowCustomInvalidMsg(true);
    } else {
      e.target.setCustomValidity(`${customInvalidMsg}`);
    }
    // invoke some error message red color
  };
  const onInputHandler = (e) => {
    e.target.setCustomValidity('');
    setShowCustomInvalidMsg(false);
  };
  return (
    <>
      <div className={cssProperties.customStyle ? `${cssProperties.customStyle} checkbox` : 'checkbox'}>
        <input
          data-testid={fieldId}
          type={fieldType}
          id={fieldId}
          className={`form-control ${cssProperties.style}`}
          name={fieldId}
          onChange={handleCheck}
          checked={optionCheck}
          value={value}
          required={forceRequired ? true : fieldValidation.required}
          onInvalid={invalidHandler}
          onInput={onInputHandler}
          disabled={
            !!(isMultipleTabsData
              && Object.keys(selectedOptions).length === 5
              && !selectedOptions[fieldId])
          }
        />
        {!optionCheck && isHiddenRequired
          && (
            <input
              type="hidden"
              id={fieldId}
              name={fieldId}
              value={value}
            />
          )}
        <label
          htmlFor={fieldId}
          className={cssProperties.labelClass ? cssProperties.labelClass : 'label-3'}
        >
          {parseHtml(fieldLabel)}
        </label>
        {fieldValidation && fieldValidation.isToolTip
          && (
            <>
              <span>{' '}</span>
              <Tooltip id="ToolTip" triggerElement={<TooltipButton icon={<IconInfo size="sm" title="info" />} />}>{fieldValidation.toolTipInfo}</Tooltip>
            </>
          )}
      </div>
      {!optionCheck
        && fieldValidation
        && fieldValidation.optionNotCheckMsg
        && (
          <div id="alert-error" className="alert alert-dismissible dls-color-warning pad-0-lr">
            <span className="icon icon-sm dls-icon-warning-filled" />
            <span>{fieldValidation.optionNotCheckMsg}</span>
          </div>
        )}
      {showCustomInvalidMsg && (
        <div id="alert-error" className="alert dls-color-warning pad-1-t col-md-12">
          <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
          <span>{customInvalidMsg}</span>
        </div>
      )}
      {
        comments && comments[fieldId] && !valueChanged
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{comments[fieldId].comment}</span>
          </div>
        )
      }
      {
        validationErrState.validationErr
        && (
          <div id="alert-error" className={cssProperties.validationErrorStyle ? cssProperties.validationErrorStyle : 'alert dls-color-warning pad-1-t col-md-12'}>
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r dls-color-warning" />
            <span>{validationErrState.validationErr}</span>
          </div>
        )
      }
    </>
  );
};

Checkbox.propTypes = {
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.string,
  fieldId: PropTypes.string,
  fieldType: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  setFormSubmissionPayload: PropTypes.func,
  selectedOptionLabels: PropTypes.objectOf(PropTypes.shape),
  setSelectedOptionLabels: PropTypes.func,
  setSelectedOptions: PropTypes.func,
  selectedOptions: PropTypes.objectOf(PropTypes.shape),
  isHiddenRequired: PropTypes.bool,
  isMultipleTabsData: PropTypes.bool,
  checkConsent: PropTypes.func,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  disabledButtons: PropTypes.objectOf(PropTypes.shape),
  setDisabledButtons: PropTypes.func,
  buttonsToDisable: PropTypes.arrayOf(PropTypes.shape),
  uncheckedValue: PropTypes.string,
  comments: PropTypes.objectOf(PropTypes.shape),
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  validations: PropTypes.arrayOf(PropTypes.shape),
  groupDynamicRequired: PropTypes.objectOf(PropTypes.shape),
  isFilledValueDisabled: PropTypes.bool,
  defaultValue: PropTypes.string,
  dispatchIdsState: PropTypes.func,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  fieldDynamicRequired: PropTypes.objectOf(PropTypes.shape),
  setShowPopUp: PropTypes.func,
};

export default Checkbox;
